import {z} from 'zod';
import {t} from 'i18next';
import {ISelectItem} from '../../../shared/types';

export const sourceKeys = [
  'dat',
  'native',
  'redactedNative',
  'text',
  'pdf',
  'tiff',
  'mp3',
  'audio',
  'other',
] as const;

export const GridMappingSchema = z.object({
  columnMappings: z
    .array(
      z.object({
        sourceFieldID: z.number().nullable(),
        sourceFieldText: z.string().nullable(),
        typeID: z.string().nullable(),
        typeText: z.string().nullable(),
        destinationID: z.number().nullable(),
        destinationText: z.string().nullable(),
        mandatory: z.boolean(),
        isNewRow: z.boolean(),
        clientUniqueIdentifierRow: z.number(),
      })
    )
    .superRefine((mappings, ctx) => {
      // ✅ ONLY validate mandatory rows
      mappings.forEach((mapping, idx) => {
        if (mapping.mandatory) {
          if (!mapping.sourceFieldID) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [idx, 'sourceFieldID'],
              message: t('ingestions.Wizard.validation.sourceFieldRequired'),
            });
          }
          if (!mapping.typeID || mapping.typeID === 'Select') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [idx, 'typeID'],
              message: t('ingestions.Wizard.validation.typeRequired'),
            });
          }
          if (!mapping.destinationID || mapping.destinationID === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [idx, 'destinationID'],
              message: t(
                'ingestions.Wizard.validation.destinationFieldRequired'
              ),
            });
          }
        }
        // ✅ REMOVED: User-added row validation
      });
    }),
});

export type GridFormValues = z.infer<typeof GridMappingSchema>;

export type SourceKey = (typeof sourceKeys)[number];

const dropdownShape = z
  .object({
    key: z.string(),
    displayValue: z.string(),
  })
  .strict();

const dropdownNullable = dropdownShape.nullable();

const normalizeDropdownValue = (val: unknown) => {
  if (val == null || typeof val !== 'object') return null;
  const obj = val as Record<string, unknown>;
  if ('key' in obj && typeof obj.key === 'string') {
    return {
      key: String(obj.key),
      displayValue: String(obj.displayValue ?? ''),
    };
  }
  return null;
};

// DAT: allow nulls; enforce presence only when enabled
const DatSourceSchema = z
  .object({
    enabled: z.boolean().default(false),
    loadFile: dropdownNullable,
    documentKey: dropdownNullable,
    isPathInDatFile: z.boolean().optional().default(false),
    filePathField: dropdownNullable.optional(),
  })
  .strict()
  .superRefine((dat, ctx) => {
    if (dat.enabled) {
      if (!dat.loadFile) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['loadFile'],
          message: t('ingestions.Wizard.validation.datLoadFileRequired'),
        });
      }
      if (!dat.documentKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['documentKey'],
          message: t('ingestions.Wizard.validation.datDocumentKeyRequired'),
        });
      }
    }
  });

const OtherSourceSchema = z
  .object({
    enabled: z.boolean().default(false),
    loadFile: dropdownNullable,
    isPathInDatFile: z.boolean().optional().default(false),
    filePathField: dropdownNullable.optional(),
    linkType: dropdownNullable.optional(),
  })
  .strict()
  .superRefine((s, ctx) => {
    // Load File is required when source is enabled AND checkbox is NOT checked
    if (s.enabled && !s.isPathInDatFile && s.loadFile === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['loadFile'],
        message: t('ingestions.Wizard.validation.loadFileRequiredWhenEnabled'),
      });
    }
    // File Path Field is required when checkbox IS checked
    if (s.isPathInDatFile && s.filePathField === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['filePathField'],
        message: t('ingestions.Wizard.validation.filePathFieldRequired'),
      });
    }
  });

const TiffSourceSchema = z
  .object({
    enabled: z.boolean().default(false),
    loadFile: dropdownNullable,
    isPathInDatFile: z.boolean().optional().default(false),
    filePathField: dropdownNullable.optional(),
    generateSearchablePdfs: z.boolean().optional().default(false), // ✅ NEW
  })
  .strict()
  .superRefine((s, ctx) => {
    if (s.enabled && !s.isPathInDatFile && s.loadFile === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['loadFile'],
        message: t('ingestions.Wizard.validation.loadFileRequiredWhenEnabled'),
      });
    }
    if (s.isPathInDatFile && s.filePathField === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['filePathField'],
        message: t('ingestions.Wizard.validation.filePathFieldRequired'),
      });
    }
  });

const RedactedNativeSourceSchema = z
  .object({
    enabled: z.boolean().default(false),
    loadFile: dropdownNullable,
    redactedTextLoadFile: dropdownNullable,
    annotationLayer: dropdownNullable,
    isPathInDatFile: z.boolean().optional().default(false),
    filePathField: dropdownNullable.optional(),
    redactedTextFilePathField: dropdownNullable.optional(),
  })
  .strict()
  .superRefine((s, ctx) => {
    if (s.enabled) {
      if (!s.isPathInDatFile && s.loadFile === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['loadFile'],
          message: t('ingestions.Wizard.validation.redactedNativeLoadFileRequired'),
        });
      }

      //  if (!s.isPathInDatFile && s.redactedTextLoadFile === null) {
      //   ctx.addIssue({
      //     code: 'custom',
      //     path: ['redactedTextLoadFile'],
      //     message: t('ingestions.Wizard.validation.redactedTextLoadFileRequired'),
      //   });
      // }

      if (s.isPathInDatFile && (s.filePathField ?? null) === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['filePathField'],
          message: t('ingestions.Wizard.validation.filePathFieldRequired'),
        });
      }

    }

    // if (s.isPathInDatFile && (s.redactedTextFilePathField ?? null) === null) {
    //     ctx.addIssue({
    //       code: 'custom',
    //       path: ['redactedTextFilePathField'],
    //       message: t('ingestions.Wizard.validation.redactedTextFilePathFieldRequired'),
    //     });
    //   }
    //   if (s.annotationLayer === null) {
    //     ctx.addIssue({
    //       code: 'custom',
    //       path: ['annotationLayer'],
    //       message: t('ingestions.Wizard.validation.redactedNativeAnnotationLayerRequired'),
    //     });
    //   }
  });

const SourcesSchema = z.object({
  dat: DatSourceSchema,
  native: OtherSourceSchema,
  text: OtherSourceSchema,
  pdf: OtherSourceSchema,
  tiff: TiffSourceSchema,
  mp3: OtherSourceSchema,
  audio: OtherSourceSchema,
  other: OtherSourceSchema,
  redactedNative: RedactedNativeSourceSchema,
});
// Update type exports
export type TiffSourceItem = z.infer<typeof TiffSourceSchema>;
export type RedactedNativeSourceItem = z.infer<typeof RedactedNativeSourceSchema>;

export const FormSchema = z
  .object({
    ingestionType: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.ingestionTypeRequired'),
      })
      .optional(),
    sourceSystem: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .optional(),
    serverLocation: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.serverLocationRequired'),
      })
      .optional(),
    sourceFolder: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.sourceFolderRequired'),
      })
      .optional(),
    fieldSeparator: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.fieldSeparatorRequired'),
      })
      .optional(),
    textQualifier: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.textQualifierRequired'),
      })
      .optional(),
    multiValue: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.multiValueRequired'),
      })
      .optional(),
    dateFormat: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .refine((v) => v != null, {
        message: t('ingestions.Wizard.validation.dateFormatRequired'),
      })
      .optional(),
    timeZone: z
      .preprocess(normalizeDropdownValue, dropdownShape.nullable())
      .optional(),
    sources: SourcesSchema,
  })
  .superRefine((form, ctx) => {
    const isOverlayOnly = form.ingestionType?.key === '2' || form.ingestionType?.key === 'Overlay';

    if (!isOverlayOnly && !form.sourceSystem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sourceSystem'],
        message: t('ingestions.Wizard.validation.sourceSystemRequired'),
      });
    }
});

export type FormValues = z.infer<typeof FormSchema>;
export type DatSourceItem = z.infer<typeof DatSourceSchema>;
export type OtherSourceItem = z.infer<typeof OtherSourceSchema>;
export type SourcesState = FormValues['sources'];

export const createDefaultSources = (): SourcesState => ({
  dat: {
    enabled: false,
    loadFile: null,
    documentKey: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  native: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  redactedNative: {
    enabled: false,
    loadFile: null,
    redactedTextLoadFile: null,
    annotationLayer: null,
    isPathInDatFile: false,
    filePathField: null,
    redactedTextFilePathField: null,
  },
  text: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  pdf: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  tiff: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
    generateSearchablePdfs: false,
  },
  mp3: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  audio: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
  },
  other: {
    enabled: false,
    loadFile: null,
    isPathInDatFile: false,
    filePathField: null,
    linkType: null,
  },
});

export const defaultSources = createDefaultSources();
